// Pure metadata table for the admin settings sub-tabs — no React or
// screen imports here so tests can read it without Jest having to
// transform every settings screen (and via them, FontAwesome's ESM).
//
// Adding a new tab:
//   1. Add an entry below (alphabetical by `key`).
//   2. Wire the screen component in `tabRegistry.ts` (single line).
// Done — AdminStack and AdminSettingsScreen pick it up automatically.
//
// Disabled tabs (no screen wired in tabRegistry) render as greyed-out
// placeholders in the catalog and are NOT mounted in AdminStack.

import type { IconProp } from '@fortawesome/fontawesome-svg-core';

export type AdminSettingsSection =
  | 'platform'
  | 'aiCore'
  | 'aiGovernance'
  | 'integrations'
  | 'engagement'
  | 'dataPerf'
  | 'diagnostics';

export interface AdminSettingsTabMeta {
  /** Stable, lowercase slug — used as React key + telemetry id. */
  key: string;
  label: string;
  description: string;
  icon: IconProp;
  /** Stack route name. Mounted in AdminStack iff a screen is wired in tabRegistry. */
  routeName: string;
  section: AdminSettingsSection;
}

export const SECTION_TITLES: Record<AdminSettingsSection, string> = {
  platform: 'Platform',
  aiCore: 'AI Core',
  aiGovernance: 'AI Governance',
  integrations: 'Integrations',
  engagement: 'Engagement',
  dataPerf: 'Data & Performance',
  diagnostics: 'Diagnostics',
};

export const SECTION_ORDER: AdminSettingsSection[] = [
  'platform',
  'aiCore',
  'aiGovernance',
  'integrations',
  'engagement',
  'dataPerf',
  'diagnostics',
];

// One entry per tab, alphabetical by `key`. Adding a tab is one new line.
export const ALL_TAB_META: AdminSettingsTabMeta[] = [
  { key: 'accountfeatures', label: 'Account Features', description: 'Per-account feature flag toggles (account_features columns).', icon: ['fad', 'toggle-on'], routeName: 'AdminSettingsAccountFeatures', section: 'platform' },
  { key: 'aifeatures', label: 'AI Features', description: 'Feature gates for AI capabilities', icon: ['fad', 'robot'], routeName: 'AdminSettingsAIFeatures', section: 'aiCore' },
  { key: 'aillm', label: 'AI / LLM', description: 'LLM interaction behavior', icon: ['fad', 'microchip'], routeName: 'AdminSettingsAILLM', section: 'aiCore' },
  { key: 'aioptimization', label: 'AI Optimization', description: 'Cost / latency tuning', icon: ['fad', 'tachometer-alt'], routeName: 'AdminSettingsAIOptimization', section: 'aiCore' },
  { key: 'appearance', label: 'App Appearance', description: 'Colors, fonts, corners, borders, backgrounds, spacing, and platform previews.', icon: ['fad', 'palette'], routeName: 'AdminSettingsAppearance', section: 'platform' },
  { key: 'approvals', label: 'Approvals', description: 'Approval workflow for governance', icon: ['fad', 'check-circle'], routeName: 'AdminSettingsApprovals', section: 'aiGovernance' },
  { key: 'benefitsources', label: 'Benefit Sources', description: 'Registry of where the benefits corpus is pulled from (SAM.gov, USA.gov Benefit Finder, …) — add, pause, or remove sources.', icon: ['fad', 'database'], routeName: 'AdminSettingsBenefitSources', section: 'dataPerf' },
  { key: 'cacheconfig', label: 'Cache Config', description: 'Cache TTL and behavior tuning', icon: ['fad', 'database'], routeName: 'AdminSettingsCacheConfig', section: 'dataPerf' },
  { key: 'cadencepairings', label: 'Cadence Pairings', description: 'Mint clinical pair-codes + manage paired Cadence installs per project (admin only).', icon: ['fad', 'qrcode'], routeName: 'AdminSettingsCadencePairings', section: 'platform' },
  { key: 'categories', label: 'Categories', description: 'Rule grouping + per-category rule assignments', icon: ['fad', 'tags'], routeName: 'AdminSettingsCategories', section: 'aiGovernance' },
  { key: 'dataapikeys', label: 'Data API Keys', description: 'Developer Data-API keys (data.eleplan.com) — plan, scopes, usage; revoke.', icon: ['fad', 'key'], routeName: 'AdminSettingsDataApi', section: 'integrations' },
  { key: 'debug', label: 'Debug', description: 'Runtime debug category controls', icon: ['fad', 'bug'], routeName: 'AdminSettingsDebug', section: 'diagnostics' },
  { key: 'embedkeys', label: 'Embed Keys', description: 'Account-scoped elk_ keys for partner-embedded chat (EHRs, patient portals).', icon: ['fad', 'code'], routeName: 'AdminSettingsEmbedKeys', section: 'integrations' },
  { key: 'fasten', label: 'Fasten', description: 'Fasten integration and sync', icon: ['fad', 'hospital'], routeName: 'AdminSettingsFasten', section: 'integrations' },
  { key: 'featureflags', label: 'Feature Flags', description: 'GLOBAL platform kill-switches (system_settings) — one level above account_features.', icon: ['fad', 'flag'], routeName: 'AdminSettingsFeatureFlags', section: 'platform' },
  { key: 'inferenceengine', label: 'Inference Engine', description: 'Autonomous inference extraction', icon: ['fad', 'brain'], routeName: 'AdminSettingsInferenceEngine', section: 'aiCore' },
  { key: 'intents', label: 'Intents', description: 'Intent definitions and routing', icon: ['fad', 'route'], routeName: 'AdminSettingsIntents', section: 'aiGovernance' },
  { key: 'leanchat', label: 'Lean Chat', description: 'Smaller chat prompts: rollout, tool descriptions, write checks', icon: ['fad', 'message-bot'], routeName: 'AdminSettingsLeanChat', section: 'aiCore' },
  { key: 'links', label: 'Links', description: 'Short-link and redirect behavior', icon: ['fad', 'link'], routeName: 'AdminSettingsLinks', section: 'integrations' },
  { key: 'locationfollowup', label: 'Location Follow-ups', description: 'Visit detection and prompts', icon: ['fad', 'location-dot'], routeName: 'AdminSettingsLocationFollowup', section: 'engagement' },
  { key: 'maintenance', label: 'Maintenance', description: 'Maintenance mode and operational safety', icon: ['fad', 'wrench'], routeName: 'AdminSettingsMaintenance', section: 'platform' },
  { key: 'models', label: 'Models', description: 'Primary/backup model selection', icon: ['fad', 'brain'], routeName: 'AdminSettingsModels', section: 'aiCore' },
  { key: 'optimization', label: 'Optimization', description: 'Generate AI rule-optimization proposals', icon: ['fad', 'sparkles'], routeName: 'AdminSettingsOptimization', section: 'dataPerf' },
  { key: 'plans', label: 'Plans', description: 'Subscription plan catalog — limits, features, price IDs per tier', icon: ['fad', 'tags'], routeName: 'AdminSettingsPlans', section: 'platform' },
  { key: 'plantasks', label: 'Plan Tasks', description: 'Setup Checklist catalog — names, descriptions, conditions, tap-routing.', icon: ['fad', 'list-check'], routeName: 'AdminSettingsPlanTasks', section: 'platform' },
  { key: 'proactiveprompts', label: 'Proactive Prompts', description: 'Proactive outreach settings', icon: ['fad', 'bell'], routeName: 'AdminSettingsProactivePrompts', section: 'engagement' },
  { key: 'reengagement', label: 'Re-engagement', description: 'Dormant-user targeting', icon: ['fad', 'user-clock'], routeName: 'AdminSettingsReengagement', section: 'engagement' },
  { key: 'rules', label: 'Rules', description: 'AI rules organized by category — view, edit, add, delete (modular sub-rules supported)', icon: ['fad', 'book'], routeName: 'AdminSettingsRules', section: 'aiGovernance' },
  { key: 'screenbuilder', label: 'Screen Builder', description: 'Build screens, rearrange existing layouts, and configure menu entries', icon: ['fad', 'layer-group'], routeName: 'AdminSettingsScreenBuilder', section: 'platform' },
  { key: 'security', label: 'Security', description: 'Authentication, access controls, security toggles', icon: ['fad', 'shield-alt'], routeName: 'AdminSettingsSecurity', section: 'platform' },
  { key: 'sms', label: 'SMS', description: 'SMS delivery and provider settings', icon: ['fad', 'comment-sms'], routeName: 'AdminSettingsSMS', section: 'integrations' },
  { key: 'ssmparams', label: 'SSM Parameters', description: 'AWS Parameter Store CRUD for this stage — biometric-gated reveal / save / delete.', icon: ['fad', 'shield-keyhole'], routeName: 'AdminSettingsSsmParams', section: 'platform' },
  { key: 'surveys', label: 'Surveys', description: 'Per-block survey administration (CRUD)', icon: ['fad', 'clipboard-list'], routeName: 'AdminSettingsSurveys', section: 'dataPerf' },
  { key: 'system', label: 'System', description: 'Core platform defaults and global flags', icon: ['fad', 'sliders-h'], routeName: 'AdminSettingsSystem', section: 'platform' },
  { key: 'vectors', label: 'Vectors', description: 'pgvector embedding fill state per table + on-demand backfill into the embeddings worker queue.', icon: ['fad', 'database'], routeName: 'AdminSettingsVectors', section: 'dataPerf' },
  { key: 'websearch', label: 'Web Search', description: 'Web fetch safety and domain governance', icon: ['fad', 'globe'], routeName: 'AdminSettingsWebSearch', section: 'integrations' },
  { key: 'weeklybriefing', label: 'Weekly Briefing', description: 'Weekly recap generation', icon: ['fad', 'newspaper'], routeName: 'AdminSettingsWeeklyBriefing', section: 'engagement' },
  { key: 'weeklydigest', label: 'Weekly Digest', description: 'Friday admin metrics email — recipients, sections, on/off.', icon: ['fad', 'chart-line'], routeName: 'AdminSettingsWeeklyDigest', section: 'engagement' },
];

export interface AdminSettingsSectionGroup<T extends AdminSettingsTabMeta = AdminSettingsTabMeta> {
  section: AdminSettingsSection;
  title: string;
  tabs: T[];
}

/**
 * Returns tabs grouped by section, in SECTION_ORDER. Sections with no
 * matching tabs are dropped — used by the search filter so empty
 * sections don't render after typing.
 */
export function getSectionsFromMeta<T extends AdminSettingsTabMeta>(
  tabs: T[],
): AdminSettingsSectionGroup<T>[] {
  return SECTION_ORDER.map((section) => ({
    section,
    title: SECTION_TITLES[section],
    tabs: tabs.filter((t) => t.section === section),
  })).filter((group) => group.tabs.length > 0);
}

/**
 * Case-insensitive search over tab label + description + key. Returns the
 * input array (same reference) when `query` is empty/whitespace-only.
 */
export function filterTabsByQuery<T extends AdminSettingsTabMeta>(
  query: string,
  tabs: T[],
): T[] {
  const q = (query || '').trim().toLowerCase();
  if (!q) return tabs;
  return tabs.filter((tab) => {
    const haystack = `${tab.label} ${tab.description} ${tab.key}`.toLowerCase();
    return haystack.includes(q);
  });
}
